import {Action,buildKit,TICK_RATE,MOVEMENT_PACE,type CharacterManifest,type FighterKit,type MoveSpec} from '@arena/shared';

const move=(name:string,damage:number,range:number,startup=6,knockback=3):MoveSpec=>({name,startup,active:3,recovery:12,range,arcDeg:100,knockback,lift:0,hitstun:13,slamGain:7,lunge:1.5,cancelFrom:8,nextLight:Action.None,superCancel:true,specialCancel:true,damage});

/**
 * Auramennone (docs/personaggi/auramennone.md): Agamemnon, king of kings, in
 * blackened bronze. A long spear and a shield; S1 sends a phalanx of three
 * soldiers marching ahead, S2 is a vain pose that, if nobody interrupts it,
 * gives him aura; the Super Slam is the maximum aura.
 */
export const AURAMENNONE:CharacterManifest={
 id:'auramennone',schemaVersion:2,displayName:'Auramennone',archetype:'brawler',
 description:'Lancia, scudo e un ego da mille navi. La falange avanza per lui, la posa gli dà l’aura. Suprema: Aura massima, sei secondi da re dei re.',
 look:{build:'heavy',accent:'#d4a340'},stats:{speed:42,power:62,resistance:65,weight:70,range:60,slamCharge:50},
 moveset:{
  light1:{...move('Lancia del re',7,1.9,5),nextLight:Action.Light2},
  light2:{...move('Affondo regale',8,2.2,6),arcDeg:70,nextLight:Action.Light3},
  light3:{...move('Scudo di Micene',12,1.7,9,8),arcDeg:140,lift:3,hitstun:18,recovery:16,cancelFrom:255},
  airAttack:{...move('Discesa regale',13,1.9,6,6),arcDeg:360,lift:2,airBounce:5,lunge:0,cancelFrom:255},
  dashAttack:{...move('Carica di scudo',10,1.6,5,7),travel:6,lunge:4,cancelFrom:255},
  special1:{...move('Falange achea',0,0,14,0),active:1,recovery:18,lunge:0,hitstun:0,slamGain:0,cooldown:12*TICK_RATE,groundOnly:true,cancelFrom:255},
  special2:{...move('Aura farming',0,0,18,0),active:1,recovery:10,lunge:0,hitstun:0,slamGain:0,cooldown:14*TICK_RATE,groundOnly:true,cancelFrom:255},
  superSlam:{...move('Aura massima',0,0,12,0),active:1,recovery:16,armorTicks:12,hitstun:0,lunge:0,slamGain:0,arcDeg:360,cancelFrom:255,superCancel:false},
 },
};

/**
 * The aura: S2 gives it for `poseTicks`, the Super Slam for `maxTicks`. While
 * it lasts his blows hit harder and blows barely move or stun him; at
 * maximum, enemies within `fearRadius` are slowed and each enemy his hits
 * reach scores once, like a Super Slam.
 */
export const AURAMENNONE_AURA={poseTicks:4*TICK_RATE,maxTicks:6*TICK_RATE,damage:1.3,maxDamage:1.5,fearRadius:3.5} as const;

const aura=(damage:number,taken:number):FighterKit=>{
 const moves=Object.fromEntries(Object.entries(AURAMENNONE.moveset).map(([k,m])=>[k,m && k!=='superSlam'?{...m,damage:(m.damage??0)*damage}:m]));
 const kit=buildKit({...AURAMENNONE,moveset:moves as CharacterManifest['moveset']});
 return Object.freeze({...kit,form:'aura' as const,response:Object.freeze({knockbackTaken:taken,hitstunTaken:taken})});
};
/** His kit while the aura lasts (S2), and at maximum (Super Slam). */
export const AURAMENNONE_AURA_KIT=aura(AURAMENNONE_AURA.damage,.35);
export const AURAMENNONE_MAX_AURA_KIT=aura(AURAMENNONE_AURA.maxDamage,.2);

/** S1: three soldiers in a line, marching straight ahead and pushing with their shields. */
export const AURAMENNONE_PHALANX={count:3,spacing:1.15,ahead:1.3,speed:4.2,hp:25,ticks:4*TICK_RATE} as const;

const hopliteManifest:CharacterManifest={...AURAMENNONE,id:'auramennone-hoplite',displayName:'Oplita',moveset:{...AURAMENNONE.moveset,
 light1:{...move('Spinta di scudo',6,1.3,7,7),active:4,recovery:20,hitstun:10,slamGain:3,lunge:0,arcDeg:120,cancelFrom:255,superCancel:false,specialCancel:false},
 special1:null,special2:null}};
export const AURAMENNONE_HOPLITE={manifest:hopliteManifest,kit:Object.freeze({...buildKit(hopliteManifest),maxSpeed:AURAMENNONE_PHALANX.speed*MOVEMENT_PACE.run}),hp:AURAMENNONE_PHALANX.hp,ticks:AURAMENNONE_PHALANX.ticks} as const;
